<script setup lang="ts" generic="T">
import { computed, ref, watch } from 'vue'

import Pagination from './Pagination/Pagination.vue'

type Props = {
  items: T[]
  reversed?: boolean
  listClass?: string | Record<string, boolean>
}

const props = defineProps<Props>()

if (props.reversed === false) {
  throw Error('reversed = false unsupported')
}

// data
const maxItemsPerPage = 10

// state
const currentPage = ref(1)

// computed
const pagesCount = computed(
  () => Math.ceil(props.items.length / maxItemsPerPage) || 1,
)

// methods
function getPageIndices(): { start: number; end: number } {
  const total = props.items.length
  const end = total - (currentPage.value - 1) * maxItemsPerPage
  const start = Math.max(0, end - maxItemsPerPage)
  return { start, end }
}

const itemsOnPage = computed(() => {
  const { start, end } = getPageIndices()
  return props.items.slice(start, end).reverse()
})

const itemsOnPageWithNeighbors = computed(() => {
  const { start, end } = getPageIndices()
  const widerStart = Math.max(0, start - 1)
  const widerEnd = Math.min(props.items.length, end + 1)
  return props.items.slice(widerStart, widerEnd).reverse()
})

function getPreviousItem(index: number): T | undefined {
  return itemsOnPageWithNeighbors.value[index - 1]
}

function getNextItem(index: number): T | undefined {
  return itemsOnPageWithNeighbors.value[index + 1]
}

// init
watch(pagesCount, () => {
  if (currentPage.value > pagesCount.value) {
    currentPage.value = 1
  }
})
</script>

<template>
  <div :class="$attrs.class">
    <template v-for="(item, index) in itemsOnPage">
      <slot
        name="item"
        :item
        :previousItem="getPreviousItem(index)"
        :nextItem="getNextItem(index)"
      >
        <div :key="index">
          {{ item }}
        </div>
      </slot>
    </template>
    <slot
      v-if="!items.length"
      name="empty"
    />
  </div>
  <Pagination
    v-model="currentPage"
    class="mt-3"
    :pagesCount
  />
</template>
